import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Divider,
  FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select,
  Stack, TextField, Typography, Pagination, Paper, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';

import Whiteboard from './Whiteboard';
import useLiveClassroomSocket from './useLiveClassroomSocket';
import {
  getAuthToken, getLiveClassroomApiRoot, liveClassroomApi, resolveBackendUrl,
} from './liveClassroomApi';

const THEME_ORANGE = '#F07828';
const BG = '#F2F2F2';
const BORDER = 'rgba(25,118,210,0.20)';
const THEME_ORANGE_2 = '#ed7f35ff';
const BLUE = '#1565C0';
const ROW_ALT = 'rgba(25,118,210,0.06)';
const ROW_HOVER = 'rgba(25,118,210,0.10)';

const BORDER_HOVER = 'rgba(25,118,210,0.45)';
const BORDER_FOCUS = 'rgba(240,120,40,0.80)';

const pageWrapSx = {
  p: 2,
  backgroundColor: BG,
  minHeight: '100vh',
};

const rootPaperSx = {
  mx: 'auto',
  borderRadius: 2,
  overflow: 'hidden',
  border: '1px solid rgba(25,118,210,0.15)',
};

const headerSx = {
  px: 2,
  py: 1.5,
  minHeight: 76,
  background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  flexWrap: 'wrap',
};

const bodySx = {
  p: 2,
  backgroundColor: BG,
};

const surfaceSx = {
  borderRadius: 2,
  backgroundColor: 'white',
  border: `1px solid ${BORDER}`,
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: BORDER_HOVER },
    '&.Mui-focused fieldset': { borderColor: BORDER_FOCUS },
  },
};

const primaryBtnSx = {
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 900,
  backgroundColor: THEME_ORANGE,
  '&:hover': { backgroundColor: THEME_ORANGE_2 },
};

const outlinedBtnSx = {
  borderRadius: 2,
  textTransform: 'none',
  fontWeight: 900,
  borderColor: 'rgba(25,118,210,0.35)',
  color: 'rgba(25,118,210,0.95)',
  '&:hover': {
    borderColor: 'rgba(25,118,210,0.55)',
    backgroundColor: 'rgba(25,118,210,0.04)',
  },
};

const sectionTitleSx = {
  fontWeight: 900,
  color: BLUE,
  mb: 1,
};


function decodeJwt() {
  try {
    const token = getAuthToken();
    if (!token) return {};
    const payload = token.split('.')[1];
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
    return JSON.parse(atob(padded)) || {};
  } catch { return {}; }
}

function currentIdentity() {
  const p = decodeJwt();
  return {
    id: String(p.id ?? ''),
    username: String(p.username ?? ''),
    campusCode: String(p.campusCode ?? ''),
    studentkey: p.studentkey ?? null,
    usertype: String(p.usertype ?? ''),
  };
}

function classLabel(c) {
  return [
    c.SubCampusCode, c.BranchCode,
    c.SemesterCode ? `Sem ${c.SemesterCode}` : '',
    c.Section ? `Section ${c.Section}` : '',
    c.Regulation, c.ClassName,
  ].filter(Boolean).join(' · ');
}

function id() {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
}
function safeFilename(v) {
  return String(v || 'report').replace(/[^a-z0-9_-]+/gi, '-').replace(/-+/g, '-').slice(0, 80);
}

// Backend Live Classroom DATETIME fields are stored and returned as IST
// strings in `YYYY-MM-DD HH:mm:ss` format. Parse them explicitly as +05:30
// so browsers in any timezone do not reinterpret them as local/UTC time.
function parseIstDateTime(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();

  // MySQL DATETIME string returned by the Live Classroom backend.
  const mysqlMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/
  );

  if (mysqlMatch) {
    const [, y, m, d, hh, mm, ss] = mysqlMatch;
    const parsed = new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}+05:30`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // Keep compatibility if an older backend returns an ISO timestamp.
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatIstDateTime(value) {
  const parsed = parseIstDateTime(value);
  if (!parsed) return '';

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(parsed);
}

async function toggleElementFullscreen(elementId) {
  try {
    if (!document.fullscreenElement) {
      await document.getElementById(elementId)?.requestFullscreen?.();
    } else {
      await document.exitFullscreen?.();
    }
  } catch (e) {
    console.error('Fullscreen:', e);
  }
}
function exportExcel(rows, name, sheet = 'Results') {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, `${safeFilename(name)}.xlsx`);
}
function exportPdf(title, rows, name) {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  let y = 40;
  pdf.setFontSize(16);
  pdf.text(String(title || 'Results'), 40, y);
  y += 26;
  pdf.setFontSize(9);

  rows.forEach((row, index) => {
    const line = Object.entries(row)
      .map(([k, v]) => `${k}: ${String(v ?? '')}`)
      .join(' | ');
    const wrapped = pdf.splitTextToSize(`${index + 1}. ${line}`, 760);
    if (y + wrapped.length * 12 > 550) {
      pdf.addPage();
      y = 40;
    }
    pdf.text(wrapped, 40, y);
    y += wrapped.length * 12 + 8;
  });
  pdf.save(`${safeFilename(name)}.pdf`);
}

function pollRows(results) {
  if (!results) return [];
  const map = Object.fromEntries((results.questions || []).map((q, i) => [q.id, { ...q, n: i + 1 }]));
  return (results.responses || []).map((r) => {
    const row = { StudentID: r.studentId || '', StudentName: r.name || '' };
    Object.entries(r.answers || {}).forEach(([qid, optionIndex]) => {
      const q = map[qid];
      if (q) row[`Q${q.n} - ${q.question}`] = q.options?.[optionIndex] ?? '';
    });
    return row;
  });
}

function questionRows(results) {
  if (!results) return [];
  const map = Object.fromEntries((results.questions || []).map((q, i) => [q.id, { ...q, n: i + 1 }]));
  return (results.responses || []).map((r) => {
    const row = { StudentID: r.studentId || '', StudentName: r.name || '' };
    Object.entries(r.answers || {}).forEach(([qid, answer]) => {
      const q = map[qid];
      if (q) row[`Q${q.n} - ${q.question}`] = answer;
    });
    return row;
  });
}


function formatReportDuration(joinedAt, endedAt = Date.now()) {
  if (!joinedAt) return '';
  const startMs = new Date(joinedAt).getTime();
  const endMs = endedAt instanceof Date ? endedAt.getTime() : new Date(endedAt).getTime();
  const safeEnd = Number.isFinite(endMs) ? endMs : Date.now();
  if (!Number.isFinite(startMs)) return '';
  const totalSeconds = Math.max(0, Math.floor((safeEnd - startMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function reportDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  }).format(parsed);
}

async function downloadFullReport({ session, participants, pollResults, questionResults, studentQuestions, format }) {
  let activity = null;

  try {
    const { data } = await liveClassroomApi.get(`/sessions/${session.SessionID}/activity`);
    activity = data?.activity || null;
  } catch (e) {
    console.error('Unable to load complete activity snapshot; using current screen state.', e);
  }

  const reportEnd = activity?.endedAt || activity?.EndedAt || session.EndedAt || Date.now();
  const allParticipants = activity?.participants || participants || [];
  const participantRows = allParticipants.map((p, index) => ({
    No: index + 1,
    StudentID: p.StudentID || p.studentId || '',
    StudentName: p.StudentName || p.name || p.username || '',
    JoinedAt: reportDateTime(p.joinedAt || p.JoinedAt || p.connectedAt || p.ConnectedAt),
    TimeInClass: formatReportDuration(
      p.joinedAt || p.JoinedAt || p.connectedAt || p.ConnectedAt,
      p.leftAt || p.LeftAt || p.disconnectedAt || p.DisconnectedAt || reportEnd
    ),
  }));

  const allPolls = activity?.polls?.length ? activity.polls : (pollResults ? [pollResults] : []);
  const allQuestionSets = activity?.classroomQuestions?.length
    ? activity.classroomQuestions
    : (questionResults ? [questionResults] : []);
  const allStudentQuestions = activity?.studentQuestions || studentQuestions || [];
  const studentQuestionRows = allStudentQuestions.map((q, index) => ({
    No: index + 1,
    StudentID: q.StudentID || q.studentId || '',
    StudentName: q.StudentName || q.name || '',
    Question: q.text || q.question || '',
    Answered: q.answered ? 'Yes' : 'No',
    AskedAt: reportDateTime(q.askedAt || q.AskedAt),
  }));

  const base = safeFilename(`${session.Title}-live-classroom-report`);

  if (format === 'excel') {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(participantRows), 'Participants');

    allPolls.forEach((poll, index) => {
      const rows = pollRows(poll);
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(rows.length ? rows : [{ Info: 'No responses' }]),
        `Poll ${index + 1}`.slice(0, 31)
      );
    });

    allQuestionSets.forEach((set, index) => {
      const rows = questionRows(set);
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(rows.length ? rows : [{ Info: 'No responses' }]),
        `Class Questions ${index + 1}`.slice(0, 31)
      );
    });

    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(studentQuestionRows.length ? studentQuestionRows : [{ Info: 'No student questions' }]),
      'Student Questions'
    );
    XLSX.writeFile(workbook, `${base}.xlsx`);
    return;
  }

  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  let firstSection = true;

  const addSection = (heading, rows) => {
    if (!firstSection) pdf.addPage();
    firstSection = false;
    let y = 42;
    pdf.setFontSize(15);
    pdf.text(String(heading), 40, y);
    y += 24;
    pdf.setFontSize(8);
    const safeRows = rows.length ? rows : [{ Info: 'No data' }];
    safeRows.forEach((row, index) => {
      const text = Object.entries(row).map(([k, v]) => `${k}: ${String(v ?? '')}`).join(' | ');
      const wrapped = pdf.splitTextToSize(`${index + 1}. ${text}`, 760);
      if (y + wrapped.length * 10 > 540) {
        pdf.addPage();
        y = 42;
        pdf.setFontSize(10);
        pdf.text(`${heading} (continued)`, 40, y);
        y += 18;
        pdf.setFontSize(8);
      }
      pdf.text(wrapped, 40, y);
      y += wrapped.length * 10 + 6;
    });
  };

  addSection(`${session.Title} - Participants`, participantRows);
  allPolls.forEach((poll, index) => addSection(`Poll ${index + 1}: ${poll.title || 'Poll'}`, pollRows(poll)));
  allQuestionSets.forEach((set, index) => addSection(`Classroom Questions ${index + 1}: ${set.title || ''}`, questionRows(set)));
  addSection('Student Questions', studentQuestionRows);
  pdf.save(`${base}.pdf`);
}

export default function LiveClassroom() {
  const me = useMemo(currentIdentity, []);
  const socket = useLiveClassroomSocket();

  const [hostContext, setHostContext] = useState({
    canHost: false, classes: [], activeSessions: [], source: null,
  });
  const [loadingContext, setLoadingContext] = useState(true);
  const [studentSessions, setStudentSessions] = useState([]);
  const [title, setTitle] = useState('Live Class');
  const [selectedClass, setSelectedClass] = useState(null);
  const [classOptions, setClassOptions] = useState([]);
  const [classSearch, setClassSearch] = useState('');
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(60);

  const [session, setSession] = useState(null);
  const [code, setCode] = useState('');
  const [participants, setParticipants] = useState([]);
  const [presenter, setPresenter] = useState(null);
  const [requests, setRequests] = useState([]);

  const [remoteStroke, setRemoteStroke] = useState(null);
  const [remoteText, setRemoteText] = useState(null);
  const [clearRevision, setClearRevision] = useState(0);

  const [document, setDocument] = useState(null);
  const [localPage, setLocalPage] = useState(1);
  const [followTeacher, setFollowTeacher] = useState(true);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [documentRevision, setDocumentRevision] = useState(0);

  const [poll, setPoll] = useState(null);
  const [pollResults, setPollResults] = useState(null);
  const [questionSet, setQuestionSet] = useState(null);
  const [questionResults, setQuestionResults] = useState(null);
  const [featured, setFeatured] = useState(null);

  const [raisedHands, setRaisedHands] = useState([]);
  const [studentQuestions, setStudentQuestions] = useState([]);
  const [reaction, setReaction] = useState(null);
  const [error, setError] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [landingTab, setLandingTab] = useState(0);
  const [minutesRemaining, setMinutesRemaining] = useState(null);
  const [extendBusy, setExtendBusy] = useState(false);
  const [qrExpanded, setQrExpanded] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [screenShareInvite, setScreenShareInvite] = useState(null);
  const [sharedScreen, setSharedScreen] = useState(null);
  const screenVideoRef = useRef(null);
  const screenPeerRef = useRef(null);
  const screenStreamRef = useRef(null);
  const [hostScreenSharing, setHostScreenSharing] = useState(false);
  const hostScreenStreamRef = useRef(null);
  const hostScreenPeersRef = useRef(new Map());
  const [teacherSharedScreen, setTeacherSharedScreen] = useState(null);
  const teacherScreenVideoRef = useRef(null);
  const teacherScreenPeerRef = useRef(null);

  const isHost = !!session && String(session.TeacherUserID) === String(me.id);
  const canDraw = isHost || (!!presenter && String(presenter) === String(me.id));

  useEffect(() => {
    if (!session?.ExpiresAt) {
      setMinutesRemaining(null);
      return undefined;
    }

    const updateRemaining = () => {
      const expires = parseIstDateTime(session.ExpiresAt);
      if (!expires) {
        setMinutesRemaining(null);
        return;
      }
      const diffMs = expires.getTime() - Date.now();
      setMinutesRemaining(Math.max(0, Math.ceil(diffMs / 60000)));
    };

    updateRemaining();
    const timer = window.setInterval(updateRemaining, 30000);
    return () => window.clearInterval(timer);
  }, [session?.ExpiresAt]);

  const hydrateState = (state = {}) => {
    setParticipants(state.participants || []);
    setPresenter(state.presenterUserId || null);
    setPoll(state.poll || null);
    setPollResults(state.pollResults || null);
    setQuestionSet(state.questionSet || null);
    setQuestionResults(state.questionResults || null);
    setFeatured(state.featured || null);
    setRaisedHands(state.raisedHands || []);
    setStudentQuestions(state.studentQuestions || []);

    if (state.document) {
      setDocument(state.document);
      setLocalPage(state.document.teacherPage || 1);
      setFollowTeacher(true);
    }

    (state.strokes || []).forEach((stroke, i) =>
      setTimeout(() => setRemoteStroke({ ...stroke, replay: `${Date.now()}-${i}` }), i));
    (state.texts || []).forEach((item, i) =>
      setTimeout(() => setRemoteText({ ...item, replay: `${Date.now()}-${i}` }), i));
  };

  const joinSocket = (s) => {
    if (!socket.connected) socket.connect();
    socket.emit('lc:join', { sessionId: s.SessionID }, (response) => {
      if (!response?.ok) {
        setError(response?.error || 'Unable to join live class.');
        return;
      }
      hydrateState(response.state);
    });
  };

  const loadHostContext = async () => {
    try {
      setLoadingContext(true);

      const [hostResponse, studentResponse] = await Promise.all([
        liveClassroomApi.get('/host-context'),
        liveClassroomApi.get('/student-active-sessions'),
      ]);

      const data = hostResponse.data || {};
      const classes = Array.isArray(data?.classes) ? data.classes : [];

      setHostContext({
        canHost: !!data?.canHost,
        classes,
        activeSessions: Array.isArray(data?.activeSessions) ? data.activeSessions : [],
        source: data?.source || null,
      });
      setClassOptions(classes);

      setStudentSessions(
        Array.isArray(studentResponse.data?.sessions)
          ? studentResponse.data.sessions
          : []
      );
    } catch (e) {
      const status = e?.response?.status;
      const backendMessage = e?.response?.data?.error;
      const target = `${getLiveClassroomApiRoot()}/host-context`;
      setError(backendMessage || (status
        ? `Unable to load Live Classroom context (HTTP ${status}).`
        : `Unable to reach Live Classroom backend at ${target}.`));
    } finally {
      setLoadingContext(false);
    }
  };

  useEffect(() => {
    loadHostContext();
    const timer = setInterval(loadHostContext, 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!hostContext.canHost) return;
    const timer = setTimeout(async () => {
      try {
        setLoadingClasses(true);
        const { data } = await liveClassroomApi.get('/classes', {
          params: { search: classSearch },
        });
        setClassOptions(Array.isArray(data?.classes) ? data.classes : []);
      } catch (e) {
        console.error('live classroom class search', e);
      } finally {
        setLoadingClasses(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [classSearch, hostContext.canHost]);

  const closeScreenPeer = () => {
    try { screenPeerRef.current?.close?.(); } catch {}
    screenPeerRef.current = null;
    setSharedScreen(null);
  };

  const stopMyScreenShare = () => {
    try { screenStreamRef.current?.getTracks?.().forEach((track) => track.stop()); } catch {}
    screenStreamRef.current = null;
    try { screenPeerRef.current?.close?.(); } catch {}
    screenPeerRef.current = null;
    socket.emit('lc:screenShareStopped');
  };

  const allowScreenShare = async () => {
    const invite = screenShareInvite;
    if (!invite) return;
    try {
      if (!navigator.mediaDevices?.getDisplayMedia) {
        throw new Error('Screen sharing is not supported by this browser.');
      }
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } },
        audio: true,
      });
      setScreenShareInvite(null);
      screenStreamRef.current = stream;
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      screenPeerRef.current = pc;
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      pc.onicecandidate = (event) => {
        if (event.candidate) socket.emit('lc:screenIceToHost', { candidate: event.candidate });
      };
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) videoTrack.onended = stopMyScreenShare;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socket.emit('lc:screenOffer', { requestId: invite.requestId, sdp: pc.localDescription });
    } catch (e) {
      if (e?.name !== 'NotAllowedError') setError(e?.message || 'Unable to start screen sharing.');
      socket.emit('lc:screenShareResponse', { requestId: invite.requestId, approved: false });
      setScreenShareInvite(null);
    }
  };

  useEffect(() => {
    const handlers = {
      'lc:participants': (v) => setParticipants(v || []),
      'lc:presenter': (v) => setPresenter(v?.userId || null),
      'lc:shareRequests': (v) => setRequests(v || []),
      'lc:screenShareInvitation': (v) => setScreenShareInvite(v || null),
      'lc:screenOffer': async (v) => {
        if (!v?.sdp || !v?.userId) return;
        closeScreenPeer();
        const pc = new RTCPeerConnection({
          iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
        });
        screenPeerRef.current = pc;
        pc.ontrack = (event) => {
          const stream = event.streams?.[0];
          if (stream) setSharedScreen({ userId: v.userId, name: v.name || v.userId, stream });
        };
        pc.onicecandidate = (event) => {
          if (event.candidate) socket.emit('lc:screenIceToStudent', { userId: v.userId, candidate: event.candidate });
        };
        await pc.setRemoteDescription(new RTCSessionDescription(v.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('lc:screenAnswer', { userId: v.userId, sdp: pc.localDescription });
      },
      'lc:screenAnswer': async (v) => {
        if (v?.sdp && screenPeerRef.current) {
          await screenPeerRef.current.setRemoteDescription(new RTCSessionDescription(v.sdp));
        }
      },
      'lc:screenIceToHost': async (v) => {
        if (v?.candidate && screenPeerRef.current) {
          try { await screenPeerRef.current.addIceCandidate(new RTCIceCandidate(v.candidate)); } catch (e) { console.error(e); }
        }
      },
      'lc:screenIceToStudent': async (v) => {
        if (v?.candidate && screenPeerRef.current) {
          try { await screenPeerRef.current.addIceCandidate(new RTCIceCandidate(v.candidate)); } catch (e) { console.error(e); }
        }
      },
      'lc:screenShareStopped': () => closeScreenPeer(),
      'lc:stopMyScreenShare': () => stopMyScreenShare(),
      'lc:hostScreenOffer': async (v) => {
        if (isHost || !v?.sdp) return;
        try { teacherScreenPeerRef.current?.close?.(); } catch {}
        const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
        teacherScreenPeerRef.current = pc;
        pc.ontrack = (event) => {
          const stream = event.streams?.[0];
          if (stream) setTeacherSharedScreen({ name: v.name || 'Teacher', stream });
        };
        pc.onicecandidate = (event) => {
          if (event.candidate) socket.emit('lc:hostScreenIceToHost', { candidate: event.candidate });
        };
        await pc.setRemoteDescription(new RTCSessionDescription(v.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('lc:hostScreenAnswer', { sdp: pc.localDescription });
      },
      'lc:hostScreenAnswer': async (v) => {
        if (!isHost || !v?.userId || !v?.sdp) return;
        const pc = hostScreenPeersRef.current.get(String(v.userId));
        if (pc) await pc.setRemoteDescription(new RTCSessionDescription(v.sdp));
      },
      'lc:hostScreenIceToStudent': async (v) => {
        if (!isHost && v?.candidate && teacherScreenPeerRef.current) {
          try { await teacherScreenPeerRef.current.addIceCandidate(new RTCIceCandidate(v.candidate)); } catch (e) { console.error(e); }
        }
      },
      'lc:hostScreenIceToHost': async (v) => {
        if (!isHost || !v?.userId || !v?.candidate) return;
        const pc = hostScreenPeersRef.current.get(String(v.userId));
        if (pc) try { await pc.addIceCandidate(new RTCIceCandidate(v.candidate)); } catch (e) { console.error(e); }
      },
      'lc:hostScreenStopped': () => {
        try { teacherScreenPeerRef.current?.close?.(); } catch {}
        teacherScreenPeerRef.current = null;
        setTeacherSharedScreen(null);
      },
      'lc:stroke': setRemoteStroke,
      'lc:text': setRemoteText,
      'lc:clear': () => setClearRevision((x) => x + 1),
      'lc:document': (v) => {
        setDocument(v || null);
        if (v) {
          setLocalPage(v.teacherPage || 1);
          setFollowTeacher(true);
          setDocumentRevision((x) => x + 1);
        }
      },
      'lc:documentPage': (v) => {
        const teacherPage = Number(v?.page || 1);
        setDocument((old) => old ? { ...old, teacherPage } : old);
        if (followTeacher) {
          setLocalPage(teacherPage);
          // Forces iframe recreation. With pageUrls this loads a different
          // one-page PDF resource instead of relying on Chrome #page behavior.
          setDocumentRevision((x) => x + 1);
        }
      },
      'lc:poll': (v) => setPoll(v || null),
      'lc:pollResults': (v) => setPollResults(v || null),
      'lc:questionSet': (v) => setQuestionSet(v || null),
      'lc:questionResults': (v) => setQuestionResults(v || null),
      'lc:featured': (v) => setFeatured(v || null),
      'lc:raisedHands': (v) => setRaisedHands(v || []),
      'lc:studentQuestions': (v) => setStudentQuestions(v || []),
      'lc:sessionExtended': (v) => {
        if (v?.ExpiresAt) {
          setSession((old) => old ? { ...old, ExpiresAt: v.ExpiresAt } : old);
        }
      },
      'lc:reaction': (v) => {
        setReaction(v);
        setTimeout(() => setReaction(null), 2200);
      },
      'lc:ended': () => {
        setSession(null);
        setParticipants([]);
        setPresenter(null);
        setRequests([]);
        setPoll(null);
        setPollResults(null);
        setQuestionSet(null);
        setQuestionResults(null);
        setFeatured(null);
        setDocument(null);
        loadHostContext();
      },
    };
    Object.entries(handlers).forEach(([event, fn]) => socket.on(event, fn));
    return () => Object.entries(handlers).forEach(([event, fn]) => socket.off(event, fn));
  }, [socket, followTeacher]);

  useEffect(() => {
    if (screenVideoRef.current && sharedScreen?.stream) {
      screenVideoRef.current.srcObject = sharedScreen.stream;
      screenVideoRef.current.play?.().catch(() => {});
    }
  }, [sharedScreen]);

  useEffect(() => {
    if (teacherScreenVideoRef.current && teacherSharedScreen?.stream) {
      teacherScreenVideoRef.current.srcObject = teacherSharedScreen.stream;
      teacherScreenVideoRef.current.play?.().catch(() => {});
    }
  }, [teacherSharedScreen]);

  useEffect(() => {
    const joinCode = new URLSearchParams(window.location.search).get('join');
    if (!joinCode || session) return;
    const normalized = joinCode.trim().toUpperCase();
    setCode(normalized);
    liveClassroomApi.get(`/join/${encodeURIComponent(normalized)}`)
      .then(({ data }) => {
        setSession(data.session);
        joinSocket(data.session);
      })
      .catch((e) => setError(e.response?.data?.error || 'Invalid or expired join code.'));
  }, [session]);

  const start = async () => {
    try {
      setError('');
      if (!hostContext.canHost) return setError('Your account does not currently have Live Classroom host scope.');
      const { data } = await liveClassroomApi.post('/sessions', {
        title: title.trim() || 'Live Class',
        classId: selectedClass?.ClassID || null,
        durationMinutes: Number(durationMinutes) || 60,
      });
      setSession(data.session);
      joinSocket(data.session);
      loadHostContext();
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to start live class.');
    }
  };

  const resume = (s) => {
    setError('');
    setSession(s);
    joinSocket(s);
  };

  const join = async () => {
    try {
      setError('');
      const normalized = code.trim().toUpperCase();
      if (!normalized) return setError('Enter a join code.');
      const { data } = await liveClassroomApi.get(`/join/${encodeURIComponent(normalized)}`);
      setSession(data.session);
      joinSocket(data.session);
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to join live class.');
    }
  };

  const end = async () => {
    if (!session) return;
    try {
      await liveClassroomApi.post(`/sessions/${session.SessionID}/end`);
      setSession(null);
      loadHostContext();
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to end live class.');
    }
  };

  const extendSession = async (minutes) => {
    if (!session || !isHost || extendBusy) return;
    try {
      setExtendBusy(true);
      setError('');
      const { data } = await liveClassroomApi.post(
        `/sessions/${session.SessionID}/extend`,
        { minutes }
      );
      if (data?.session?.ExpiresAt) {
        setSession((old) => old ? { ...old, ExpiresAt: data.session.ExpiresAt } : old);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to extend live class.');
    } finally {
      setExtendBusy(false);
    }
  };

  const uploadPresentation = async (file) => {
    if (!file || !session || !isHost) return;
    try {
      setUploadBusy(true);
      setError('');
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await liveClassroomApi.post(
        `/sessions/${session.SessionID}/document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }
      );
      socket.emit('lc:setDocument', { document: data.document });
      setDocument(data.document);
      setLocalPage(1);
      setFollowTeacher(true);
      setDocumentRevision((x) => x + 1);
    } catch (e) {
      setError(e.response?.data?.error || 'Unable to upload presentation.');
    } finally {
      setUploadBusy(false);
    }
  };

  const clearPresentation = () => {
    if (!session || !isHost || !document) return;

    const clearNow = () => {
      socket.emit('lc:setDocument', { document: null }, (response) => {
        if (response && response.ok === false) {
          setError(response.error || 'Unable to clear presentation.');
          return;
        }
        setDocument(null);
        setLocalPage(1);
        setFollowTeacher(true);
        setDocumentRevision((x) => x + 1);
      });
    };

    if (window.confirm('Clear the currently uploaded presentation for everyone in this live class?')) {
      clearNow();
    }
  };

  const closeHostScreenPeers = () => {
    for (const pc of hostScreenPeersRef.current.values()) {
      try { pc?.close?.(); } catch {}
    }
    hostScreenPeersRef.current.clear();
  };

  const stopHostScreenShare = () => {
    try { hostScreenStreamRef.current?.getTracks?.().forEach((track) => track.stop()); } catch {}
    hostScreenStreamRef.current = null;
    closeHostScreenPeers();
    setHostScreenSharing(false);
    socket.emit('lc:hostScreenStopped');
  };

  const createHostScreenPeer = async (participant) => {
    const userId = String(participant?.id || participant?.userId || '');
    const stream = hostScreenStreamRef.current;
    if (!userId || userId === String(me.id) || !stream || hostScreenPeersRef.current.has(userId)) return;

    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
    });
    hostScreenPeersRef.current.set(userId, pc);
    stream.getTracks().forEach((track) => pc.addTrack(track, stream));
    pc.onicecandidate = (event) => {
      if (event.candidate) socket.emit('lc:hostScreenIceToStudent', { userId, candidate: event.candidate });
    };
    pc.onconnectionstatechange = () => {
      if (['failed', 'closed'].includes(pc.connectionState)) {
        try { pc.close(); } catch {}
        hostScreenPeersRef.current.delete(userId);
      }
    };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    socket.emit('lc:hostScreenOffer', { userId, sdp: pc.localDescription });
  };

  const shareMyScreen = async () => {
    if (!session || !isHost) return;
    if (hostScreenSharing) return stopHostScreenShare();
    try {
      setError('');
      if (!navigator.mediaDevices?.getDisplayMedia) throw new Error('Screen sharing is not supported by this browser.');
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: { ideal: 15, max: 30 } }, audio: true,
      });
      hostScreenStreamRef.current = stream;
      setHostScreenSharing(true);
      const videoTrack = stream.getVideoTracks?.()[0];
      if (videoTrack) videoTrack.onended = stopHostScreenShare;
      await Promise.all((participants || []).map((p) => createHostScreenPeer(p)));
    } catch (e) {
      if (e?.name !== 'NotAllowedError') setError(e?.message || 'Unable to share your screen.');
      setHostScreenSharing(false);
      hostScreenStreamRef.current = null;
      closeHostScreenPeers();
    }
  };

  // Add newly joined students to an already-running teacher screen share.
  useEffect(() => {
    if (!isHost || !hostScreenSharing || !hostScreenStreamRef.current) return;
    (participants || []).forEach((p) => createHostScreenPeer(p).catch(console.error));
  }, [participants, isHost, hostScreenSharing]);

  const changePage = (page, teacherAction) => {
    if (!document) return;
    const next = Math.min(document.totalPages, Math.max(1, Number(page) || 1));
    setLocalPage(next);
    setDocumentRevision((x) => x + 1);
    if (isHost && teacherAction) {
      socket.emit('lc:documentPage', { page: next });
    } else if (!isHost) {
      setFollowTeacher(false);
    }
  };
  const syncToTeacher = () => {
    if (!document) return;
    const teacherPage = Number(document.teacherPage || 1);
    setFollowTeacher(true);
    setLocalPage(teacherPage);
    // Recreate presentation iframe so the actual one-page PDF resource is
    // loaded even if Chrome cached the previous embedded PDF.
    setDocumentRevision((x) => x + 1);
  };

  const generateAiAnalysis = async () => {
    if (!session || !isHost) return;

    try {
      setAiBusy(true);
      setAiAnalysis('');
      const { data } = await liveClassroomApi.post(
        `/sessions/${session.SessionID}/ai-analysis`
      );
      setAiAnalysis(data?.analysis || 'No analysis returned.');
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || 'Unable to generate AI analysis.');
    } finally {
      setAiBusy(false);
    }
  };

  const runReportDownload = async (format) => {
    try {
      setReportBusy(true);
      setError('');
      await downloadFullReport({
        session, participants, pollResults, questionResults, studentQuestions, format,
      });
      setReportDialogOpen(false);
    } catch (e) {
      console.error('Download report:', e);
      setError(e?.response?.data?.error || e?.message || 'Unable to download report.');
    } finally {
      setReportBusy(false);
    }
  };

  // QR intentionally points to a public backend-served guest page instead of
  // the protected React /LiveClassroom route. Authenticated UniFLOO users can
  // still use the normal Live Classroom page; guests only receive a
  // session-scoped Socket.IO token.
  const joinUrl = session
    ? new URL(
        `${getLiveClassroomApiRoot()}/public/join/${encodeURIComponent(session.JoinCode)}`,
        window.location.origin
      ).toString()
    : '';

  if (!session) {
    const canStart = !!hostContext.canHost;

    return (
      <Box sx={pageWrapSx}>
        <Paper elevation={5} sx={rootPaperSx}>
          <Box sx={headerSx}>
            <Box>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                Live Classroom
              </Typography>

              <Typography
                sx={{
                  opacity: 0.9,
                  fontSize: '0.85rem',
                }}
              >
                Smart board, presentations, whiteboard, polls, classroom questions and live student interaction
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="center"
            >
              {!!studentSessions.length && (
                <Chip
                  label={`${studentSessions.length} live class${studentSessions.length === 1 ? '' : 'es'} available`}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 900,
                    border: '1px solid rgba(255,255,255,0.35)',
                  }}
                />
              )}

              {!!hostContext.activeSessions?.length && (
                <Chip
                  label={`${hostContext.activeSessions.length} active class${hostContext.activeSessions.length === 1 ? '' : 'es'}`}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 900,
                    border: '1px solid rgba(255,255,255,0.35)',
                  }}
                />
              )}
            </Stack>
          </Box>

          <Box sx={bodySx}>
            {loadingContext && <LinearProgress sx={{ mb: 2 }} />}

            {error && (
              <Alert
                severity="error"
                onClose={() => setError('')}
                sx={{
                  borderRadius: 2,
                  border: '1px solid rgba(244,67,54,0.25)',
                  backgroundColor: 'rgba(244,67,54,0.04)',
                  mb: 2,
                }}
              >
                {error}
              </Alert>
            )}

            <Paper elevation={0} sx={surfaceSx}>
              <Tabs
                value={landingTab}
                onChange={(_e, value) => setLandingTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  px: 1,
                  minHeight: 48,
                  borderBottom: `1px solid ${BORDER}`,
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontWeight: 800,
                  },
                  '& .Mui-selected': {
                    color: `${THEME_ORANGE} !important`,
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: THEME_ORANGE,
                  },
                }}
              >
                <Tab label="Live Classes" />
                <Tab label="Start Live Class" disabled={!canStart} />
                <Tab label="Join With Code" />
              </Tabs>

              {landingTab === 0 && (
                <Box sx={{ p: 2 }}>
                  {!!studentSessions.length && (
                    <Box sx={{ mb: hostContext.activeSessions?.length ? 2 : 0 }}>
                      <Typography sx={sectionTitleSx}>
                        Live classes available to join
                      </Typography>

                      <Stack spacing={1}>
                        {studentSessions.map((s) => (
                          <Stack
                            key={s.SessionID}
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            spacing={1}
                            sx={{
                              p: 1.25,
                              border: `1px solid ${BORDER}`,
                              borderRadius: 2,
                              backgroundColor: '#fff',
                              '&:hover': {
                                borderColor: BORDER_HOVER,
                                backgroundColor: ROW_HOVER,
                              },
                            }}
                          >
                            <Box>
                              <Typography fontWeight={900}>
                                {s.Title}
                              </Typography>

                              <Typography variant="body2">
                                {s.ClassName} · {s.BranchCode} · Sem {s.Semester}
                                {s.Section ? ` · Section ${s.Section}` : ''}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {s.StudentID} · {s.StudentName}
                              </Typography>
                              {(s.StartedAt || s.ExpiresAt) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {s.StartedAt ? `Started: ${formatIstDateTime(s.StartedAt)} IST` : ''}
                                  {s.StartedAt && s.ExpiresAt ? ' · ' : ''}
                                  {s.ExpiresAt ? `Ends: ${formatIstDateTime(s.ExpiresAt)} IST` : ''}
                                </Typography>
                              )}
                            </Box>

                            <Button
                              variant="contained"
                              sx={primaryBtnSx}
                              onClick={() => resume(s)}
                            >
                              Join
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {!!hostContext.activeSessions?.length && (
                    <Box>
                      <Typography sx={sectionTitleSx}>
                        Your active live classes
                      </Typography>

                      <Stack spacing={1}>
                        {hostContext.activeSessions.map((s) => (
                          <Stack
                            key={s.SessionID}
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            spacing={1}
                            sx={{
                              p: 1.25,
                              border: `1px solid ${BORDER}`,
                              borderRadius: 2,
                              backgroundColor: '#fff',
                            }}
                          >
                            <Box>
                              <Typography fontWeight={900}>
                                {s.Title}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Code: {s.JoinCode}
                              </Typography>
                              {(s.StartedAt || s.ExpiresAt) && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {s.StartedAt ? `Started: ${formatIstDateTime(s.StartedAt)} IST` : ''}
                                  {s.StartedAt && s.ExpiresAt ? ' · ' : ''}
                                  {s.ExpiresAt ? `Ends: ${formatIstDateTime(s.ExpiresAt)} IST` : ''}
                                </Typography>
                              )}
                            </Box>

                            <Button
                              variant="outlined"
                              sx={outlinedBtnSx}
                              onClick={() => resume(s)}
                            >
                              Rejoin
                            </Button>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {!studentSessions.length &&
                    !hostContext.activeSessions?.length && (
                      <Alert severity="info">
                        There are no active Live Classroom sessions available right now.
                      </Alert>
                    )}
                </Box>
              )}

              {landingTab === 1 && canStart && (
                <Box sx={{ p: 2 }}>
                  <Typography sx={sectionTitleSx}>
                    Start a live class
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Search for a class or start a general session without linking a class.
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Session title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        sx={inputSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={5}>
                      <Autocomplete
                        options={classOptions}
                        value={selectedClass}
                        loading={loadingClasses}
                        getOptionLabel={classLabel}
                        isOptionEqualToValue={(a, b) =>
                          String(a.ClassID) === String(b.ClassID)
                        }
                        onChange={(_e, value) => setSelectedClass(value)}
                        inputValue={classSearch}
                        onInputChange={(_e, value) => setClassSearch(value)}
                        filterOptions={(x) => x}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Search class (optional)"
                            placeholder="Example: KW,12,BCME"
                            sx={inputSx}
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Duration</InputLabel>
                        <Select
                          value={durationMinutes}
                          label="Duration"
                          onChange={(e) =>
                            setDurationMinutes(e.target.value)
                          }
                        >
                          {[30, 45, 60, 90, 120, 180].map((minutes) => (
                            <MenuItem key={minutes} value={minutes}>
                              {minutes} minutes
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        sx={primaryBtnSx}
                        onClick={start}
                      >
                        Start live class
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {landingTab === 2 && (
                <Box sx={{ p: 2 }}>
                  <Typography sx={sectionTitleSx}>
                    Join a live class
                  </Typography>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <TextField
                      size="small"
                      label="Join code"
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.toUpperCase())
                      }
                      inputProps={{ maxLength: 6 }}
                      sx={{ ...inputSx, minWidth: 220 }}
                    />

                    <Button
                      variant="outlined"
                      sx={outlinedBtnSx}
                      onClick={join}
                    >
                      Join class
                    </Button>
                  </Stack>
                </Box>
              )}
            </Paper>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={pageWrapSx}>
      <Dialog open={reportDialogOpen} onClose={reportBusy ? undefined : () => setReportDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 900, color: BLUE }}>Download Full Report</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Choose the report format. The full classroom activity will be included, not only the currently visible paginated entries.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={reportBusy} onClick={() => setReportDialogOpen(false)}>Cancel</Button>
          <Button disabled={reportBusy} variant="outlined" sx={outlinedBtnSx} onClick={() => runReportDownload('pdf')}>
            {reportBusy ? 'Preparing...' : 'PDF'}
          </Button>
          <Button disabled={reportBusy} variant="contained" sx={primaryBtnSx} onClick={() => runReportDownload('excel')}>
            {reportBusy ? 'Preparing...' : 'Excel'}
          </Button>
        </DialogActions>
      </Dialog>
      {screenShareInvite && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2100, bgcolor: 'rgba(0,0,0,.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
          <Paper sx={{ width: 'min(520px, 94vw)', p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={900} color={BLUE}>Teacher is requesting your screen</Typography>
            <Typography sx={{ mt: 1.5 }}>
              {screenShareInvite.teacherName || 'Your teacher'} wants to view your entire screen. Click Allow, then choose the screen you want to share in the browser window.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button variant="outlined" onClick={() => {
                socket.emit('lc:screenShareResponse', { requestId: screenShareInvite.requestId, approved: false });
                setScreenShareInvite(null);
              }}>Decline</Button>
              <Button variant="contained" sx={primaryBtnSx} onClick={allowScreenShare}>Allow screen sharing</Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {sharedScreen && isHost && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2050, bgcolor: '#111', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, bgcolor: '#fff' }}>
            <Typography fontWeight={900} color={BLUE}>{sharedScreen.name} · Screen sharing</Typography>
            <Button variant="contained" color="error" onClick={() => {
              socket.emit('lc:stopStudentScreenShare', { userId: sharedScreen.userId });
              closeScreenPeer();
            }}>Stop viewing</Button>
          </Stack>
          <Box component="video" ref={screenVideoRef} autoPlay playsInline sx={{ width: '100%', height: 'calc(100vh - 56px)', objectFit: 'contain', bgcolor: '#000' }} />
        </Box>
      )}

      {teacherSharedScreen && !isHost && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2050, bgcolor: '#111', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1, bgcolor: '#fff' }}>
            <Typography fontWeight={900} color={BLUE}>{teacherSharedScreen.name || 'Teacher'} · Shared screen</Typography>
            <Typography variant="caption" color="text.secondary">Live teacher screen</Typography>
          </Stack>
          <Box component="video" ref={teacherScreenVideoRef} autoPlay playsInline sx={{ width: '100%', height: 'calc(100vh - 56px)', objectFit: 'contain', bgcolor: '#000' }} />
        </Box>
      )}

      {qrExpanded && (
        <Box
          onClick={() => setQrExpanded(false)}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1800,
            bgcolor: 'rgba(0,0,0,0.68)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              bgcolor: '#fff',
              borderRadius: 3,
              p: 3,
              maxWidth: '92vw',
              maxHeight: '92vh',
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography fontWeight={900} color={BLUE} sx={{ mb: 2 }}>
              Scan to Join Live Classroom
            </Typography>
            <Box sx={{ maxWidth: '80vw', overflow: 'auto' }}>
              <QRCodeSVG value={joinUrl} size={340} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5 }}>
              Click outside this popup to close
            </Typography>
          </Box>
        </Box>
      )}

      {reaction && (
        <Box sx={{ position: 'fixed', zIndex: 1600, right: 30, top: 90, fontSize: 52 }}>
          {reaction.emoji}
        </Box>
      )}

      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ width: '100%' }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {session.Title}
              </Typography>
              <Typography sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                Code: {session.JoinCode} · {participants.length} connected
                {session.ExpiresAt ? ` · Ends ${formatIstDateTime(session.ExpiresAt)} IST` : ''}
                {session.ClassID ? ` · Class ${session.ClassID}` : ' · General session'}
              </Typography>
            </Box>
            {isHost && (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Box
                  onClick={() => setQrExpanded(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') setQrExpanded(true);
                  }}
                  title="Click to enlarge QR code"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    p: .5,
                    borderRadius: 1,
                    cursor: 'zoom-in',
                  }}
                >
                  <QRCodeSVG value={joinUrl} size={72} />
                </Box>
                <Button
                  variant="contained"
                  onClick={shareMyScreen}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: hostScreenSharing ? '#d32f2f' : '#fff',
                    color: hostScreenSharing ? '#fff' : THEME_ORANGE,
                    '&:hover': {
                      backgroundColor: hostScreenSharing ? '#b71c1c' : 'rgba(255,255,255,0.92)',
                    },
                  }}
                >
                  {hostScreenSharing ? 'Stop Sharing Screen' : 'Share My Screen'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={() => setReportDialogOpen(true)}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: '#fff',
                    color: THEME_ORANGE,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.92)',
                    },
                  }}
                >
                  Download Full Report
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  disabled={aiBusy}
                  onClick={generateAiAnalysis}
                  sx={{
                    ...primaryBtnSx,
                    backgroundColor: '#fff',
                    color: THEME_ORANGE,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.92)',
                    },
                  }}
                >
                  {aiBusy ? 'Analyzing...' : 'Generate AI Analysis'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={end}
                  sx={{
                    ...outlinedBtnSx,
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.60)',
                    '&:hover': {
                      borderColor: '#fff',
                      backgroundColor: 'rgba(255,255,255,0.12)',
                    },
                  }}
                >
                  End class
                </Button>
              </Stack>
            )}
          </Stack>
        </Box>

        <Box sx={bodySx}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {isHost && minutesRemaining !== null && minutesRemaining <= 5 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1}>
              {[15, 30, 60].map((minutes) => (
                <Button
                  key={minutes}
                  size="small"
                  variant="outlined"
                  disabled={extendBusy}
                  onClick={() => extendSession(minutes)}
                >
                  +{minutes} min
                </Button>
              ))}
            </Stack>
          }
        >
          {minutesRemaining > 0
            ? `This live class ends in about ${minutesRemaining} minute${minutesRemaining === 1 ? '' : 's'}. Would you like to extend it?`
            : 'The scheduled class time has been reached. Extend the session to keep it available to new participants.'}
        </Alert>
      )}

      {aiAnalysis && (
        <Card sx={{ ...surfaceSx, mb: 2 }}>
          <CardContent>
            <Typography sx={sectionTitleSx}>AI Classroom Analysis</Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>{aiAnalysis}</Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            {featured && (
              <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}><CardContent>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  spacing={1}
                >
                  <Typography fontWeight={800} color={THEME_ORANGE}>
                    Featured answer — {featured.studentId ? `${featured.studentId} · ` : ''}{featured.name}
                  </Typography>
                  {isHost && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={outlinedBtnSx}
                      onClick={() => socket.emit('lc:clearFeatured')}
                    >
                      Clear Featured Answer
                    </Button>
                  )}
                </Stack>
                <Typography
                  fontWeight={700}
                  sx={{
                    mt: 1,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                  }}
                >
                  {featured.question}
                </Typography>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                  }}
                >
                  {featured.text}
                </Typography>
              </CardContent></Card>
            )}

            <PresentationPanel
              isHost={isHost} document={document} page={localPage}
              following={followTeacher} uploading={uploadBusy} revision={documentRevision}
              onUpload={uploadPresentation} onPageChange={changePage}
              onSync={syncToTeacher} onClear={clearPresentation}
            />

            <Card sx={surfaceSx}>
              <CardContent>
                <Typography sx={sectionTitleSx}>Shared Whiteboard</Typography>
                <Whiteboard
                  canDraw={canDraw}
                  allowEraser={isHost}
                  onStroke={(v) => socket.emit('lc:stroke', v)}
                  onText={(v) => socket.emit('lc:text', v)}
                  onClear={isHost ? () => socket.emit('lc:clear') : undefined}
                  onRequestDraw={
                    !isHost && String(presenter || '') !== String(me.id)
                      ? () => socket.emit('lc:requestShare', { message: 'I want to use the whiteboard' })
                      : undefined
                  }
                  onStopShare={
                    !isHost && String(presenter || '') === String(me.id)
                      ? () => socket.emit('lc:stopShare')
                      : undefined
                  }
                  remoteStroke={remoteStroke}
                  remoteText={remoteText}
                  clearRevision={clearRevision}
                />
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Stack spacing={2}>
            <ParticipantsPanel participants={participants} isHost={isHost} socket={socket} />
            <InteractionPanel socket={socket} isHost={isHost} me={me}
              raisedHands={raisedHands} studentQuestions={studentQuestions} />
            {isHost && <ShareRequests requests={requests} socket={socket} presenter={presenter} />}
            <PollPanel isHost={isHost} poll={poll} results={pollResults}
              socket={socket} />
            <QuestionPanel isHost={isHost} questionSet={questionSet} results={questionResults}
              socket={socket} />
          </Stack>
        </Grid>
      </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

function presentationPageUrl(document, page) {
  if (!document) return '';

  const pageIndex = Math.max(0, Number(page || 1) - 1);
  const pageUrl = Array.isArray(document.pageUrls)
    ? document.pageUrls[pageIndex]
    : null;

  if (pageUrl) {
    return resolveBackendUrl(pageUrl);
  }

  // Backwards compatibility for presentations uploaded before pageUrls existed.
  const base = resolveBackendUrl(document.url);
  if (!base) return '';

  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}lcpage=${page}&lcv=${Date.now()}#page=${page}&view=FitH`;
}

function PresentationPanel({
  isHost,
  document,
  page,
  following,
  uploading,
  revision,
  onUpload,
  onPageChange,
  onSync,
  onClear,
}) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullDocumentMode, setFullDocumentMode] = useState(false);
  const fullscreenId = 'live-classroom-presentation';

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(
        window.document?.fullscreenElement?.id === fullscreenId
      );
    };

    window.document.addEventListener(
      'fullscreenchange',
      onFullscreenChange
    );

    return () =>
      window.document.removeEventListener(
        'fullscreenchange',
        onFullscreenChange
      );
  }, []);

  const downloadFullPresentation = async () => {
    if (!document?.url) return;

    try {
      const response = await fetch(resolveBackendUrl(document.url), {
        credentials: 'include',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = document.name || 'presentation.pdf';
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('Presentation download:', e);
      window.open(resolveBackendUrl(document.url), '_blank', 'noopener,noreferrer');
    }
  };

  const togglePresentationFullscreen = async () => {
    try {
      if (!window.document?.fullscreenElement) {
        await window.document
          .getElementById(fullscreenId)
          ?.requestFullscreen?.();
      } else {
        await window.document.exitFullscreen?.();
      }
    } catch (e) {
      console.error('Presentation fullscreen:', e);
    }
  };

  if (!document && !isHost) return null;

  return (
    <Card
      id={fullscreenId}
      sx={{
        ...surfaceSx,
        overflow: 'hidden',
        '&:fullscreen': {
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          maxHeight: 'none',
          borderRadius: 0,
          bgcolor: BG,
        },
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          ...(isFullscreen
            ? {
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
              }
            : {}),
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{
            mb: 1,
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography sx={sectionTitleSx}>
              Presentation
            </Typography>

            {document && (
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {document.name} · Page {page} of {document.totalPages}
              </Typography>
            )}
          </Box>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
          >
            {document && !fullDocumentMode && (
              <>
                <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  disabled={page <= 1}
                  onClick={() => onPageChange(page - 1, isHost)}
                >
                  Previous
                </Button>

                <TextField
                  size="small"
                  type="number"
                  label="Page"
                  value={page}
                  onChange={(e) => onPageChange(e.target.value, isHost)}
                  inputProps={{ min: 1, max: document.totalPages }}
                  sx={{ ...inputSx, width: 105 }}
                />

                <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  disabled={page >= document.totalPages}
                  onClick={() => onPageChange(page + 1, isHost)}
                >
                  Next
                </Button>
              </>
            )}

            {document && !isHost && (
              <>
                <Button
                  variant={fullDocumentMode ? 'contained' : 'outlined'}
                  sx={fullDocumentMode ? primaryBtnSx : outlinedBtnSx}
                  onClick={() => setFullDocumentMode((v) => !v)}
                >
                  {fullDocumentMode ? 'Return to page view' : 'Full document'}
                </Button>

                <Button
                  variant="outlined"
                  sx={outlinedBtnSx}
                  onClick={downloadFullPresentation}
                >
                  Download PDF
                </Button>

                <Button
                  variant="contained"
                  sx={primaryBtnSx}
                  onClick={() => {
                    setFullDocumentMode(false);
                    onSync();
                  }}
                >
                  Sync with teacher
                </Button>

                <Chip
                  size="small"
                  color={following && !fullDocumentMode ? 'success' : 'default'}
                  label={
                    fullDocumentMode
                      ? 'Browsing full document'
                      : following
                      ? 'Following teacher'
                      : `Teacher page: ${document.teacherPage || 1}`
                  }
                />
              </>
            )}

            {document && isHost && (
              <Button
                variant="outlined"
                sx={outlinedBtnSx}
                onClick={downloadFullPresentation}
              >
                Download PDF
              </Button>
            )}

            {isHost && document && (
              <Button
                variant="outlined"
                color="error"
                disabled={uploading}
                onClick={onClear}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 900,
                }}
              >
                Clear Presentation
              </Button>
            )}

            {isHost && (
              <Button
                component="label"
                variant="outlined"
                disabled={uploading}
                sx={outlinedBtnSx}
              >
                {uploading
                  ? 'Uploading...'
                  : 'Upload PDF / PPT'}

                <input
                  hidden
                  type="file"
                  accept=".pdf,.ppt,.pptx"
                  onChange={(e) => {
                    const file =
                      e.target.files?.[0];

                    if (file) onUpload(file);

                    e.target.value = '';
                  }}
                />
              </Button>
            )}

            {document && (
              <Button
                variant="outlined"
                sx={outlinedBtnSx}
                onClick={togglePresentationFullscreen}
              >
                {isFullscreen
                  ? 'Exit full screen'
                  : 'Full screen'}
              </Button>
            )}
          </Stack>
        </Stack>

        {uploading && (
          <LinearProgress
            sx={{ mb: 1, flexShrink: 0 }}
          />
        )}

        {document && (
          <>
            <Box
              key={`${document.id}-${page}-${revision}`}
              component="iframe"
              title="Live presentation"
              src={
                fullDocumentMode
                  ? resolveBackendUrl(document.url)
                  : presentationPageUrl(document, page)
              }
              sx={{
                width: '100%',
                height: isFullscreen
                  ? 'auto'
                  : { xs: 460, md: 650 },
                flex: isFullscreen ? 1 : 'none',
                minHeight: isFullscreen ? 0 : undefined,
                border: `1px solid ${BORDER}`,
                borderRadius: 1,
                bgcolor: '#fff',
                display: 'block',
              }}
            />


          </>
        )}
      </CardContent>
    </Card>
  );
}

function formatJoinedTimer(joinedAt, nowMs) {
  if (!joinedAt) return '00:00';

  const joinedMs = new Date(joinedAt).getTime();
  if (!Number.isFinite(joinedMs)) return '00:00';

  const totalSeconds = Math.max(0, Math.floor((nowMs - joinedMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function ParticipantsPanel({ participants, isHost, socket }) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 5;

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const filtered = participants.filter((p) => {
    const haystack = `${p.StudentID || ''} ${p.StudentName || ''} ${p.name || ''} ${p.username || ''}`.toLowerCase();
    return haystack.includes(search.trim().toLowerCase());
  });
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const requestScreenShare = (p) => {
    if (!isHost) return;
    const participantName = p.StudentName || p.name || p.username || p.StudentID || 'this student';
    if (!window.confirm(`Ask ${participantName} to share their entire screen?`)) return;
    socket.emit('lc:requestStudentScreenShare', { userId: p.id || p.userId }, (response) => {
      if (!response?.ok) window.alert(response?.error || 'Unable to request screen sharing.');
    });
  };

  return (
    <Card sx={surfaceSx}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={900} color={BLUE} sx={{ flexShrink: 0 }}>
            Participants ({participants.length})
          </Typography>
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            sx={{ ...inputSx, ml: 'auto !important', width: 150, '& .MuiInputBase-input': { py: .65, fontSize: 13 } }}
          />
        </Stack>

        <Stack spacing={.5} sx={{ mt: 1, maxHeight: 220, overflow: 'auto' }}>
          {visible.map((p) => {
            const participantName = p.StudentID
              ? `${p.StudentID} · ${p.StudentName || p.name || ''}`
              : p.StudentName || p.name || p.username || p.id;
            return (
              <Chip
                key={p.id || p.userId || p.StudentID}
                onClick={isHost ? () => requestScreenShare(p) : undefined}
                clickable={isHost}
                title={isHost ? 'Click to request full-screen sharing' : undefined}
                label={`${participantName} · ${formatJoinedTimer(p.joinedAt, nowMs)}`}
              />
            );
          })}
          {!filtered.length && <Typography variant="caption" color="text.secondary">No matching participants.</Typography>}
        </Stack>
        {filtered.length > pageSize && (
          <Pagination
            page={page}
            count={pages}
            onChange={(_e, value) => setPage(value)}
            size="small"
            sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}
          />
        )}
      </CardContent>
    </Card>
  );
}

function InteractionPanel({ socket, isHost, me, raisedHands, studentQuestions }) {
  const [question, setQuestion] = useState('');
  const [page, setPage] = useState(1);
  const [expandedStudentQuestions, setExpandedStudentQuestions] = useState({});
  const pageSize = 3;
  const orderedQuestions = useMemo(() => [...studentQuestions].sort((a, b) =>
    Number(!!a.answered) - Number(!!b.answered) ||
    Number(a.askedAt || 0) - Number(b.askedAt || 0)
  ), [studentQuestions]);
  const pages = Math.max(1, Math.ceil(orderedQuestions.length / pageSize));
  const visible = orderedQuestions.slice((page - 1) * pageSize, page * pageSize);
  const myHand = raisedHands.some((x) => String(x.userId) === String(me.id));

  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  return (
    <Card sx={surfaceSx}><CardContent>
      <Typography variant="h6" fontWeight={900} color={BLUE}>Live interaction</Typography>
      <Divider sx={{ my: 1 }} />

      {!isHost && (
        <>
          <Button fullWidth variant={myHand ? 'contained' : 'outlined'}
            onClick={() => socket.emit('lc:raiseHand', { raised: !myHand })} sx={{ mb: 1 }}>
            {myHand ? '✋ Lower hand' : '✋ Raise hand'}
          </Button>
          <Stack direction="row" spacing={.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
            {['👍','👏','❤️','🎉','😂','🤔'].map((emoji) => (
              <Button key={emoji} size="small" variant="outlined"
                onClick={() => socket.emit('lc:reaction', { emoji })}>{emoji}</Button>
            ))}
          </Stack>
          <TextField fullWidth size="small" multiline minRows={2}
            label="Ask the teacher a question" value={question}
            onChange={(e) => setQuestion(e.target.value)} />
          <Button fullWidth variant="outlined" disabled={!question.trim()} sx={{ mt: 1 }}
            onClick={() => socket.emit('lc:studentQuestion', { text: question }, (r) => {
              if (r?.ok) setQuestion('');
            })}>
            Send question
          </Button>
        </>
      )}

      {isHost && (
        <>
          <Typography fontWeight={800}>Raised hands ({raisedHands.length})</Typography>
          {raisedHands.length
            ? (
              <Stack spacing={.5} sx={{ my: 1 }}>
                {raisedHands.map((x) => (
                  <Chip
                    key={x.userId}
                    label={`✋ ${x.studentId ? `${x.studentId} · ` : ''}${x.name}`}
                    clickable={isHost}
                    onClick={
                      isHost
                        ? () => socket.emit('lc:clearRaisedHand', { userId: x.userId })
                        : undefined
                    }
                    onDelete={
                      isHost
                        ? () => socket.emit('lc:clearRaisedHand', { userId: x.userId })
                        : undefined
                    }
                    title={isHost ? 'Click to clear raised hand' : undefined}
                    sx={isHost ? { cursor: 'pointer' } : undefined}
                  />
                ))}
              </Stack>
            )
            : <Typography variant="body2" color="text.secondary">No hands raised.</Typography>}
          <Divider sx={{ my: 1 }} />
          <Typography fontWeight={800}>Student questions</Typography>

          <Stack spacing={1} sx={{ mt: 1 }}>
            {visible.length ? visible.map((q, i) => (
              <Box key={q.id} sx={{
                p: 1, borderRadius: 1,
                bgcolor: i % 2 === 0 ? ROW_ALT : '#fff',
                '&:hover': { bgcolor: ROW_HOVER },
              }}>
                <Typography variant="caption" fontWeight={800}>
                  {q.studentId ? `${q.studentId} · ` : ''}{q.name}
                </Typography>
                <Typography
                  variant="body2"
                  onClick={() => setExpandedStudentQuestions((old) => ({
                    ...old,
                    [q.id]: !old[q.id],
                  }))}
                  title={expandedStudentQuestions[q.id] ? 'Click to collapse' : 'Click to expand'}
                  sx={{
                    my: .5,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    textOverflow: expandedStudentQuestions[q.id] ? 'clip' : 'ellipsis',
                    whiteSpace: expandedStudentQuestions[q.id] ? 'pre-wrap' : 'nowrap',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {q.text}
                </Typography>
                <Button size="small"
                  variant={q.answered ? 'contained' : 'outlined'}
                  color={q.answered ? 'success' : 'primary'}
                  onClick={() => socket.emit('lc:markStudentQuestionAnswered', {
                    questionId: q.id, answered: !q.answered,
                  })}>
                  {q.answered ? 'Answered' : 'Mark answered'}
                </Button>
              </Box>
            )) : <Typography variant="body2" color="text.secondary">No questions yet.</Typography>}
          </Stack>

          {orderedQuestions.length > pageSize && (
            <Pagination page={page} count={pages}
              onChange={(_e, value) => setPage(value)} sx={{ mt: 1 }} />
          )}
        </>
      )}
    </CardContent></Card>
  );
}

function ShareRequests({ requests, socket, presenter }) {
  return (
    <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}><CardContent>
      <Typography variant="h6" fontWeight={800}>Whiteboard requests</Typography>
      <Divider sx={{ my: 1 }} />
      {presenter && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Chip color="success" label="Student presenter active" />
          <Button size="small" color="error" variant="outlined"
            onClick={() => socket.emit('lc:stopShare')}>
            Stop sharing
          </Button>
        </Stack>
      )}
      {!requests.length ? <Typography color="text.secondary">No pending requests.</Typography>
        : requests.map((r) => (
          <Stack key={r.userId} direction="row" justifyContent="space-between"
            alignItems="center" sx={{ py: .5 }}>
            <Typography>{r.studentId ? `${r.studentId} · ` : ''}{r.name}</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={() => socket.emit('lc:shareDecision',
                { userId: r.userId, approved: true })}>Allow</Button>
              <Button size="small" color="error" onClick={() => socket.emit('lc:shareDecision',
                { userId: r.userId, approved: false })}>Reject</Button>
            </Stack>
          </Stack>
        ))}
    </CardContent></Card>
  );
}

function PollPanel({ isHost, poll, results, socket, sessionTitle }) {
  const [title, setTitle] = useState('Live Poll');
  const [questions, setQuestions] = useState([
    { id: id(), question: '', options: ['Yes', 'No'] },
  ]);
  const [answers, setAnswers] = useState({});

  const update = (i, patch) =>
    setQuestions((old) => old.map((x, n) => n === i ? { ...x, ...patch } : x));

  return (
    <Card sx={{ border: `1px solid ${BORDER}`, borderRadius: 2 }}><CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={900} color={BLUE}>Live poll</Typography>
        {isHost && poll && (
          <Button size="small" color="error" onClick={() => socket.emit('lc:clearPoll')}>Clear</Button>
        )}
      </Stack>
      <Divider sx={{ my: 1 }} />

      {isHost && (
        <>
          <TextField fullWidth size="small" label="Poll title" value={title}
            onChange={(e) => setTitle(e.target.value)} sx={{ mb: 1 }} />
          <Stack spacing={1}>
            {questions.map((q, i) => (
              <Box key={q.id} sx={{ p: 1, border: '1px solid rgba(0,0,0,.1)', borderRadius: 1 }}>
                <TextField fullWidth size="small" label={`Question ${i + 1}`} value={q.question}
                  onChange={(e) => update(i, { question: e.target.value })} sx={{ mb: 1 }} />
                <Stack spacing={.5}>
                  {q.options.map((o, oi) => (
                    <TextField key={oi} size="small" label={`Option ${oi + 1}`} value={o}
                      onChange={(e) => {
                        const next = [...q.options];
                        next[oi] = e.target.value;
                        update(i, { options: next });
                      }} />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: .5 }}>
                  <Button size="small" disabled={q.options.length >= 8}
                    onClick={() => update(i, { options: [...q.options, ''] })}>+ Option</Button>
                  <Button size="small" color="error" disabled={questions.length <= 1}
                    onClick={() => setQuestions((old) => old.filter((_x, n) => n !== i))}>
                    Delete question
                  </Button>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => setQuestions((old) => [
              ...old, { id: id(), question: '', options: ['', ''] },
            ])}>+ Question</Button>
            <Button size="small" variant="contained"
              onClick={() => socket.emit('lc:createPollSet', { title, questions })}>Start poll</Button>
            {poll?.open && <Button size="small" color="warning"
              onClick={() => socket.emit('lc:closePoll')}>Close poll</Button>}
          </Stack>
        </>
      )}

      {poll && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography fontWeight={800}>{poll.title}</Typography>
          {poll.questions.map((q, i) => (
            <Box key={q.id} sx={{ p: 1, bgcolor: '#f7f7f7', borderRadius: 1 }}>
              <Typography fontWeight={700}>{i + 1}. {q.question}</Typography>
              {!isHost && poll.open && (
                <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                  <InputLabel>Answer</InputLabel>
                  <Select value={answers[q.id] ?? ''} label="Answer"
                    onChange={(e) => setAnswers((old) => ({ ...old, [q.id]: Number(e.target.value) }))}>
                    {q.options.map((o, oi) => <MenuItem key={oi} value={oi}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {isHost && q.options.map((o, oi) => (
                <Typography key={oi} variant="body2">{o}: {q.counts?.[oi] || 0}</Typography>
              ))}
            </Box>
          ))}
          {!isHost && poll.open && (
            <Button variant="contained"
              onClick={() => socket.emit('lc:submitPoll', { answers })}>Submit poll</Button>
          )}
          <Typography variant="caption" color="text.secondary">
            Respondents: {poll.totalRespondents || 0} · {poll.open ? 'Open' : 'Closed'}
          </Typography>
        </Stack>
      )}
    </CardContent></Card>
  );
}

function QuestionPanel({ isHost, questionSet, results, socket }) {
  const [title, setTitle] = useState('Classroom Questions');
  const [questions, setQuestions] = useState([{ id: id(), question: '' }]);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(1);
  const [expandedClassroomAnswers, setExpandedClassroomAnswers] = useState({});
  const pageSize = 5;
  const responses = results?.responses || [];
  const pages = Math.max(1, Math.ceil(responses.length / pageSize));
  const visible = responses.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  return (
    <Card sx={surfaceSx}><CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight={900} color={BLUE}>Classroom questions</Typography>
        {isHost && questionSet && (
          <Button size="small" color="error" onClick={() => socket.emit('lc:clearQuestionSet')}>Clear</Button>
        )}
      </Stack>
      <Divider sx={{ my: 1 }} />

      {isHost && (
        <>
          <TextField fullWidth size="small" label="Question set title" value={title}
            onChange={(e) => setTitle(e.target.value)} sx={{ mb: 1 }} />
          <Stack spacing={1}>
            {questions.map((q, i) => (
              <Stack key={q.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                <TextField fullWidth size="small" multiline minRows={2}
                  label={`Question ${i + 1}`} value={q.question}
                  onChange={(e) => setQuestions((old) =>
                    old.map((x, n) => n === i ? { ...x, question: e.target.value } : x))} />
                <Button size="small" color="error" variant="outlined"
                  disabled={questions.length <= 1}
                  onClick={() => setQuestions((old) => old.filter((_x, n) => n !== i))}>
                  Delete
                </Button>
              </Stack>
            ))}
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            <Button size="small" onClick={() => setQuestions((old) =>
              [...old, { id: id(), question: '' }])}>+ Question</Button>
            <Button size="small" variant="contained"
              onClick={() => socket.emit('lc:createQuestionSet', { title, questions })}>Start questions</Button>
            {questionSet?.open && <Button size="small" color="warning"
              onClick={() => socket.emit('lc:closeQuestionSet')}>Close questions</Button>}
          </Stack>
        </>
      )}

      {questionSet && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <Typography fontWeight={800}>{questionSet.title}</Typography>
          {questionSet.questions.map((q, i) => (
            <Box key={q.id}>
              <Typography fontWeight={700}>{i + 1}. {q.question}</Typography>
              {!isHost && questionSet.open && (
                <TextField fullWidth size="small" multiline minRows={2}
                  value={answers[q.id] || ''}
                  onChange={(e) => setAnswers((old) => ({ ...old, [q.id]: e.target.value }))}
                  sx={{ mt: .5 }} />
              )}
            </Box>
          ))}
          {!isHost && questionSet.open && (
            <Button variant="contained"
              onClick={() => socket.emit('lc:submitQuestionSet', { answers })}>Submit answers</Button>
          )}

          {isHost && !!visible.length && (
            <>
              <Stack spacing={1}>
                {visible.map((r, ri) => (
                  <Box key={r.userId} sx={{
                    p: 1, borderRadius: 1,
                    bgcolor: ri % 2 === 0 ? ROW_ALT : '#fff',
                  }}>
                    <Typography fontWeight={800}>
                      {r.studentId ? `${r.studentId} · ` : ''}{r.name}
                    </Typography>
                    {questionSet.questions.map((q) => {
                      const answer = r.answers?.[q.id];
                      if (!answer) return null;
                      return (
                        <Box key={q.id} sx={{ mt: .5 }}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            display="block"
                            onClick={() => setExpandedClassroomAnswers((old) => ({
                              ...old,
                              [`${r.userId}-${q.id}`]: !old[`${r.userId}-${q.id}`],
                            }))}
                            title={expandedClassroomAnswers[`${r.userId}-${q.id}`] ? 'Click to collapse' : 'Click to expand'}
                            sx={{
                              cursor: 'pointer',
                              overflow: 'hidden',
                              textOverflow: expandedClassroomAnswers[`${r.userId}-${q.id}`] ? 'clip' : 'ellipsis',
                              whiteSpace: expandedClassroomAnswers[`${r.userId}-${q.id}`] ? 'normal' : 'nowrap',
                            }}
                          >
                            {q.question}
                          </Typography>
                          <Typography
                            variant="body2"
                            onClick={() => setExpandedClassroomAnswers((old) => ({
                              ...old,
                              [`${r.userId}-${q.id}`]: !old[`${r.userId}-${q.id}`],
                            }))}
                            title={expandedClassroomAnswers[`${r.userId}-${q.id}`] ? 'Click to collapse' : 'Click to expand'}
                            sx={{
                              cursor: 'pointer',
                              overflow: 'hidden',
                              textOverflow: expandedClassroomAnswers[`${r.userId}-${q.id}`] ? 'clip' : 'ellipsis',
                              whiteSpace: expandedClassroomAnswers[`${r.userId}-${q.id}`] ? 'pre-wrap' : 'nowrap',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            {answer}
                          </Typography>
                          <Button size="small"
                            onClick={() => socket.emit('lc:featureAnswer',
                              { userId: r.userId, questionId: q.id })}>
                            Share on screen
                          </Button>
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Stack>
              {responses.length > pageSize && (
                <Pagination page={page} count={pages}
                  onChange={(_e, value) => setPage(value)} sx={{ mt: 1 }} />
              )}
            </>
          )}
        </Stack>
      )}
    </CardContent></Card>
  );
}
